import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PrStatusBadge } from "@/components/returns/PrStatusBadge";
import { SupplierDocumentsCard } from "@/components/supplier-documents/SupplierDocumentsCard";
import { money } from "@/lib/constants";
import {
  ArrowLeft, RefreshCw, Send, X, AlertCircle, PackageX, Info,
} from "lucide-react";

function Row({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] uppercase text-muted-foreground tracking-wider">{label}</dt>
      <dd className="text-sm text-slate-800">{value || "—"}</dd>
    </div>
  );
}

export default function DevolucionDetalle() {
  const { prId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [pr, setPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const canEmit = hasPermission("purchase_returns:emit");
  const canCancel = hasPermission("purchase_returns:cancel");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/purchase-returns/${prId}`);
      setPr(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [prId]);

  useEffect(() => { load(); }, [load]);

  async function emit() {
    setBusy(true);
    try {
      const { data } = await api.post(`/purchase-returns/${prId}/emit`);
      setPr(data);
      const msg = data.ap_adjusted
        ? `Devolución emitida · CxP ajustada por ${money(data.ap_adjustment_amount)}`
        : `Devolución emitida · CxP NO ajustada: ${data.ap_adjustment_pending_reason || "revisar"}`;
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!cancelReason.trim()) { toast.error("Motivo requerido"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/purchase-returns/${prId}/cancel`, { reason: cancelReason.trim() });
      setPr(data);
      setCancelOpen(false);
      setCancelReason("");
      toast.success("Devolución cancelada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading || !pr) {
    return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;
  }

  const canEmitNow = canEmit && pr.status === "borrador";
  const canCancelNow = canCancel && pr.status === "borrador";

  return (
    <div className="space-y-6" data-testid="page-devolucion-detalle">
      <Button variant="ghost" size="sm" onClick={() => navigate("/devoluciones")} data-testid="pr-back">
        <ArrowLeft className="h-4 w-4 mr-2" /> Volver a devoluciones
      </Button>

      <PageHeader
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <PackageX className="h-6 w-6 text-tenant" />
            <span className="mono text-tenant">{pr.folio}</span>
            <PrStatusBadge status={pr.status} />
          </span>
        }
        description={<span>{pr.supplier_name} · {pr.branch_name} · OC <b className="mono">{pr.po_folio}</b></span>}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="pr-refresh">
              <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
            </Button>
            {canEmitNow && (
              <Button size="sm" className="btn-tenant" disabled={busy} onClick={emit} data-testid="pr-emit">
                <Send className="h-4 w-4 mr-1" /> Emitir devolución
              </Button>
            )}
            {canCancelNow && (
              <Button
                size="sm" variant="outline" className="text-red-600 hover:text-red-700"
                onClick={() => setCancelOpen(true)} data-testid="pr-cancel"
              >
                <X className="h-4 w-4 mr-1" /> Cancelar
              </Button>
            )}
          </div>
        }
      />

      {pr.status === "emitida" && !pr.ap_adjusted && pr.ap_adjustment_pending_reason && (
        <Alert className="bg-amber-50 border-amber-200" data-testid="pr-ap-pending-alert">
          <AlertCircle className="h-4 w-4 text-amber-700" />
          <AlertDescription className="text-sm text-amber-900">
            <b>Ajuste a CxP pendiente:</b> {pr.ap_adjustment_pending_reason}
            {" "}Monto teórico: <b className="mono">{money(pr.ap_adjustment_amount)}</b>.
          </AlertDescription>
        </Alert>
      )}

      <Card className="border-border">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Info className="h-4 w-4" /> Datos generales</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Row label="Fecha" value={pr.return_date} />
            <Row label="Motivo" value={pr.reason} />
            <Row label="Creada por" value={pr.created_by_name} />
            <Row label="Emitida por" value={pr.emitted_by_name} />
            <Row label="Cancelada por" value={pr.cancelled_by_name} />
            <Row label="Subtotal (sin IVA, informativo)" value={money(pr.subtotal)} />
            <Row label="Ajuste teórico c/IVA" value={money(pr.ap_adjustment_amount)} />
            <Row label="CxP ajustada" value={pr.status === "emitida" ? (pr.ap_adjusted ? "Sí" : "Pendiente") : "—"} />
            {pr.notes && <div className="col-span-2 md:col-span-4"><Row label="Notas" value={pr.notes} /></div>}
            {pr.cancel_reason && <div className="col-span-2 md:col-span-4"><Row label="Motivo de cancelación" value={pr.cancel_reason} /></div>}
          </dl>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="text-base">Partidas devueltas</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-0">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="text-right">Cant.</TableHead>
                <TableHead className="text-right">Costo</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
                <TableHead className="text-right">Total c/IVA</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(pr.lines || []).map((l, idx) => (
                <TableRow key={l.id} data-testid={`pr-detail-line-${idx}`}>
                  <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{l.description}</div>
                    {l.notes && <div className="text-xs text-muted-foreground">{l.notes}</div>}
                  </TableCell>
                  <TableCell className="text-right mono text-sm">{Number(l.qty_to_return).toFixed(2)}</TableCell>
                  <TableCell className="text-right mono text-sm">{money(l.unit_cost_snapshot)}</TableCell>
                  <TableCell className="text-right text-xs">{Math.round((l.iva_rate_snapshot || 0) * 100)}%</TableCell>
                  <TableCell className="text-right mono text-sm">{money(l.subtotal)}</TableCell>
                  <TableCell className="text-right mono text-sm">{money(l.total_with_iva)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/compras/${pr.purchase_order_id}`)} data-testid="pr-open-po">
          Ver OC completa →
        </Button>
      </div>

      <SupplierDocumentsCard entityType="return" entityId={pr.id} testIdPrefix="pr-docs" />

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="pr-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar devolución</DialogTitle>
            <DialogDescription>Solo se puede cancelar en <b>borrador</b>.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Motivo *</Label>
            <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="pr-cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Cerrar</Button>
            <Button className="btn-tenant" disabled={busy || !cancelReason.trim()} onClick={cancel} data-testid="pr-cancel-submit">
              Cancelar devolución
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
